import React, { memo } from "react";
import PropTypes from "prop-types";
import { NodeResizer } from "@xyflow/react";
import { BiCopy } from "react-icons/bi";
import { FiEdit } from "react-icons/fi";
import { MdDeleteForever, MdDragIndicator } from "react-icons/md";
import { FaExternalLinkAlt } from "react-icons/fa";

// Chart Components
import ChartFetch from "@/components/HomeCharts/ChartFetch";
import DashboardImage from "@/components/LayoutCharts/DashboardImage";
import DialFetch from "@/components/LayoutCharts/DialFetch";
import LayoutAlarmNotification from "@/components/LayoutCharts/LayoutAlarmNotification";
import LayoutTextBox from "@/components/LayoutCharts/LayoutTextBox";
import TableChart from "@/components/LayoutCharts/TableChart";

// Component Registry for tile rendering
const COMPONENT_MAP = {
  // chart fetch
  bar: ChartFetch,
  line: ChartFetch,
  pie: ChartFetch,
  comboBar: ChartFetch,

  // dial fetch
  dial: DialFetch,
  splitCounter: DialFetch,
  tank: DialFetch,
  dynamicBox: DialFetch,
  loadSummary: DialFetch,
  thermometer: DialFetch,
  counter: DialFetch,

  // no extra api
  textBox: LayoutTextBox,
  html: LayoutTextBox,
  image: DashboardImage,
  alarmNotification: LayoutAlarmNotification,

  // others
  table: TableChart,
};

const TileNode = memo(({ id, data, selected }) => {
  const {
    tile,
    mode = "view",
    isDark = false,
    isSelected = false,
    isMobile = false,
    displayData = {},
    dialChangeKey = 0,
    dashboardName = "",
    onCopy,
    onEdit,
    onRemove,
    onSelect,
    onLinkedTab,
    onResizeEnd,
  } = data;

  const Component = COMPONENT_MAP[tile?.component];
  const isEditable = mode !== "view" && !isMobile;

  const targetTabId = displayData?.linkTabId || tile?.linkTabId;
  const hasLink = Boolean(targetTabId);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        position: "relative",
        pointerEvents: "auto",
        cursor: !isEditable && hasLink ? "pointer" : undefined,
      }}
    >
      <NodeResizer
        isVisible={selected && isEditable && !isMobile}
        minWidth={140}
        minHeight={60}
        lineClassName="live-node__resize-line"
        handleClassName="live-node__resize-handle"
        onResizeEnd={(event, params) => {
          onResizeEnd?.(id, params);
        }}
      />

      <div
        className={`dashboardTiles ${tile?.isBorderHidden ? "" : "withBorder"} ${isSelected ? "active" : ""} ${hasLink && !isEditable ? "has-tab-link" : ""}`}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
          background: isDark ? "#1f1f1f" : "#ffffff",
          cursor: isEditable && !isMobile ? "move" : hasLink ? "pointer" : "default",
          pointerEvents: "auto",
          transition: "box-shadow 0.15s ease, border-color 0.15s ease",
        }}
      >
        <div className="position-relative h-100 w-100 d-flex align-items-center">
          {isEditable && !isMobile && (
            <div
              className="position-absolute d-flex align-items-center"
              style={{
                top: 8,
                left: 10,
                zIndex: 30,
                color: isDark ? "#888" : "#aaa",
                cursor: "grab",
                fontSize: 16,
                padding: "2px",
                borderRadius: 4,
              }}
              title="Drag to move tile"
            >
              <MdDragIndicator />
            </div>
          )}
          {isEditable && (
            <div
              className="d-flex position-absolute nodrag"
              style={{
                top: 10,
                right: 15,
                zIndex: 30,
                background: isDark ? "rgba(31,31,31,0.85)" : "rgba(255,255,255,0.85)",
                padding: "2px 6px",
                borderRadius: 6,
                backdropFilter: "blur(4px)",
              }}
            >
              <BiCopy
                className="clickBtn text-secondary me-2 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onCopy?.(tile);
                }}
                title="Copy Tile"
              />
              <FiEdit
                className="clickBtn text-secondary me-2 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.(tile);
                }}
                title="Edit Tile"
              />
              <MdDeleteForever
                className="clickBtn text-danger cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove?.(tile?._id);
                }}
                title="Delete Tile"
              />
            </div>
          )}

          <div
            className="p-2 h-100 w-100"
            style={{ overflow: "hidden", cursor: !isEditable && hasLink ? "pointer" : undefined }}
            onDoubleClick={() => tile?.component !== "textBox" && onSelect?.(tile?._id)}
          >
            {Component ? (
              <div
                className="relative h-100 w-100"
                style={{ cursor: !isEditable && hasLink ? "pointer" : undefined }}
                onClick={() => !isEditable && onLinkedTab?.(targetTabId)}
              >
                <Component
                  data={displayData}
                  type={tile?.component}
                  dialChangeKey={dialChangeKey}
                  isSelected={isSelected}
                  dashboardName={dashboardName}
                />
                {hasLink && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 2,
                      right: 10,
                      zIndex: 20,
                      color: "#cac8c8",
                      cursor: !isEditable ? "pointer" : "default",
                    }}
                    title="Linked Tab"
                  >
                    <FaExternalLinkAlt />
                  </div>
                )}
              </div>
            ) : (
              <div className="text-muted small p-2">Component missing: {tile?.component}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

TileNode.displayName = "TileNode";
TileNode.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  selected: PropTypes.bool,
};

export default TileNode;
